/**
 * The one path an orchestrator turn takes: build the prompt, announce it, send it.
 *
 * Every flow turn rebuilds the full `# Flow Execution Context` prompt and hands
 * it to an agent driver. There are four such call sites — the stimulus bus and
 * the inline sub-flow runner, once each in the runner's `serve.ts` (hosted
 * sessions) and in `run-flow.ts` (CLI runs) — and nothing about the prompt used
 * to reach the event stream, so a consumer saw the agent replying to input it
 * could not read. Routing all four through {@link driveOrchestratorTurn} is what
 * keeps the announcement from drifting away from what is actually sent.
 *
 * Lives with the flow connector rather than the runner because the CLI entry in
 * this same directory is one of the four callers, and `factory-assets` must not
 * take a static import on `runner`.
 *
 * @docLink packages/factory-assets/flow#orchestrator-turn
 */

import type { FlowExecution, FlowPromptEvent } from "@skaile/workspaces/types";
import type { FlowDefinition } from "./engine/types.js";
import {
  buildOrchestratorPrompt,
  describeStimulus,
  type TurnStimulus,
} from "./prompt-fragments.js";

/** What one orchestrator turn is about — the inputs both the prompt and the event derive from. */
export interface FlowTurnContext {
  /** Definition of the flow being driven — the child definition for an inline sub-flow turn. */
  flow: FlowDefinition;
  /** Live snapshot the prompt is built from. */
  execution: FlowExecution;
  /** Why the runner is driving this turn. */
  stimulus: TurnStimulus;
}

/** Host seams {@link driveOrchestratorTurn} needs to announce and deliver a turn. */
export interface FlowTurnSinks {
  /** Puts the announcement on the session's event stream. */
  emit: (event: FlowPromptEvent) => void;
  /** Hands the prompt to the driver and resolves when the turn ends. */
  deliver: (prompt: string) => Promise<void>;
}

/**
 * The `nodeId` a stimulus concerns, or `undefined` for flow-scoped stimuli.
 *
 * Only the three node-scoped kinds carry one; `user_message` names a sender
 * rather than a node, and the rest are whole-run signals.
 */
function stimulusNodeId(stimulus: TurnStimulus): string | undefined {
  switch (stimulus.kind) {
    case "approval_received":
    case "input_received":
    case "retry_requested":
      return stimulus.nodeId;
    default:
      return undefined;
  }
}

/**
 * Build the `flow_prompt` event describing one orchestrator turn.
 *
 * `prompt` is stored verbatim — callers must pass the same string they hand to
 * the driver, never a re-rendered one, or the host renders something the agent
 * never saw. The stimulus is reduced to its kind plus the node it concerns
 * because its payload (user text, node input) is already inside `prompt`.
 *
 * Pure so the field mapping is unit-testable without booting a session.
 *
 * @docLink packages/factory-assets/flow#orchestrator-turn
 */
export function buildFlowPromptEvent(input: FlowTurnContext & { prompt: string }): FlowPromptEvent {
  const nodeId = stimulusNodeId(input.stimulus);
  return {
    type: "flow_prompt",
    runId: input.execution.runId,
    flowId: input.flow.id,
    flowName: input.flow.name,
    stimulus: input.stimulus.kind,
    stimulusLabel: describeStimulus(input.stimulus),
    ...(nodeId === undefined ? {} : { nodeId }),
    prompt: input.prompt,
  };
}

/**
 * Build one orchestrator prompt, announce it, then deliver it to the agent.
 *
 * The prompt is built once and the same binding is both put on the event and
 * passed to `deliver`, which is what makes the emitted `prompt` byte-identical
 * to what the driver receives.
 *
 * `emit` runs before `deliver` is even called, so the announcement always
 * precedes that turn's reply events. A caller that must not prompt at all
 * (turn gated off, run already terminal) has to return before calling this —
 * reaching here means the prompt is going out.
 *
 * @docLink packages/factory-assets/flow#orchestrator-turn
 */
export async function driveOrchestratorTurn(input: FlowTurnContext & FlowTurnSinks): Promise<void> {
  const prompt = buildOrchestratorPrompt(input.flow, input.execution, input.stimulus);
  input.emit(
    buildFlowPromptEvent({
      flow: input.flow,
      execution: input.execution,
      stimulus: input.stimulus,
      prompt,
    }),
  );
  await input.deliver(prompt);
}
